/* eslint-disable prefer-promise-reject-errors */
import { sendCodeTimeKey } from '@/config/base';
import { useLocales } from '@/Locales';
import { getVerifyCode, httpSystemSetting } from '@/services/user';
import { userInfoAtom } from '@/store/atom';
import { validIsMobile } from '@/utils';
import { clearSystemUserInfo, removeToken, setLocalStorage } from '@/utils/cookies';
import { CheckboxChangeEvent } from '@sinohealth/butterfly-ui-antd/lib/checkbox';
import { Button, Checkbox, Form, Input, message } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import styles from '../../index.less';
import ProtocolModal, { ProtocolModalRef } from '../ProtocolModal';

const iconUser = <span className={classNames(styles['form-icon'], 'iconfont icon-a-1')} />;
const iconPwd = <span className={classNames(styles['form-icon'], 'iconfont icon-a-2')} />;
const sendCodeTimeOut = 60;

interface AccountFormProps {
  loading?: boolean;
  onLogin: (params: any) => Promise<void>;
  onForget: () => void;
}

const AccountForm: React.FC<AccountFormProps> = ({ onLogin, loading, onForget }) => {
  const [form] = Form.useForm();
  const [showPwd, setShowPwd] = useState(false);
  const [agree, setAgree] = useState(false);
  const [enableMFA, setEnableMFA] = useState(false);
  const [loadingSetting, setLoadingSetting] = useState(false);
  const [phoneNo, setPhoneNo] = useState('');
  const [canSendCode, setCanSendCode] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const setUserInfo = useSetRecoilState(userInfoAtom);
  // 表单校验后，所有字段有值且勾选协议才可登录
  const [canLogin, setCanLogin] = useState(false);
  const protocolModalRef = useRef<ProtocolModalRef>(null);

  const { t } = useLocales();

  /**
   * 处理登录提交
   * @param values 表单数据
   */
  const handleLogin = async (values: any) => {
    try {
      const { password, username, phone, captcha, channel } = values;

      // 检查是否同意协议
      if (!agree) {
        // 未同意协议，弹出协议确认弹窗
        protocolModalRef.current?.open({ password, username, phone, captcha, channel });
        return;
      }

      // 调用登录接口
      await onLogin({ password, username, phone, captcha, channel });
    } catch (error) {
      console.error('登录处理失败:', error);
    }
  };

  const onFieldsChange = () => {
    const values = form.getFieldsValue();
    const errors = form.getFieldsError();

    // 获取第一个错误信息
    // const firstErrorMessage = getFirstErrorMessage(errors);
    // if (firstErrorMessage) {
    //   console.log('第一个错误信息:', firstErrorMessage);

    //   // 如果启用实时错误提示，则立即显示
    //   message.error(firstErrorMessage);
    // }

    setCanLogin(!!values.username && !!values.password && agree);
  };

  const onCheckboxChange = (e: CheckboxChangeEvent) => {
    setAgree(e.target.checked);
    const values = form.getFieldsValue();
    setCanLogin(!!values.username && !!values.password && e.target.checked);
  };

  const handleSendSmsCode = async () => {
    if (!phoneNo) {
      message.error(t('手机号码不能为空'));
      return;
    }
    try {
      await getVerifyCode(phoneNo);
      setLocalStorage(sendCodeTimeKey, Date.now());
      setTimeLeft(sendCodeTimeOut);
      setCanSendCode(false);
    } finally {
      console.log(1);
    }
  };

  const validAccount = (rule: any, value: any) => {
    if (value === '') {
      message.error(t('账号名称不能为空'));
      return Promise.reject(t('账号名称不能为空'));
    }
    return Promise.resolve();
  };

  const validPassword = (rule: any, value: any) => {
    if (value === '') {
      message.error(t('密码不能为空'));
      return Promise.reject(t('密码不能为空'));
    }
    return Promise.resolve();
  };

  const validPhoneNo = (rule: any, value: any) => {
    if (value === '') {
      message.error(t('手机号码不能为空'));
      return Promise.reject(t('手机号码不能为空'));
    }
    if (value.length === 11 && !validIsMobile(value)) {
      message.error(t('手机号格式不正确'));
      return Promise.reject(t('手机号格式不正确'));
    }
    return Promise.resolve();
  };

  const agreementValid = (rule: any, val: any) => {
    if (val) {
      return Promise.resolve();
    }
    // message.error(t('请先阅读勾选《用户服务协议》和《隐私政策》'));
    return Promise.reject();
  };

  useEffect(() => {
    clearSystemUserInfo();
    httpSystemSettingReq();
    removeToken();
    setUserInfo(null); // 进入登录页就清空全局状态的用户信息
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      setCanSendCode(false);
      setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 900);
    } else {
      setCanSendCode(true);
    }
  }, [timeLeft]);

  /** 获取登录配置 */
  const httpSystemSettingReq = async () => {
    try {
      setLoadingSetting(true);
      const res: any = await httpSystemSetting();
      // console.log(res);
      setEnableMFA(res.enableMFA || false);
    } catch (err) {
      console.log(ErrorEvent);
    } finally {
      setLoadingSetting(false);
    }
  };

  return (
    <>
      <Form
        form={form}
        className={styles.loginForm}
        onFieldsChange={onFieldsChange}
        onFinish={handleLogin}
      >
        <Form.Item name="channel" hidden noStyle initialValue="account" />
        <Form.Item
          name="username"
          rules={[{ required: true, validator: validAccount, message: t('请输入账号名称') }]}
        >
          <Input
            prefix={iconUser}
            placeholder={t('请输入账户名称')}
            size="large"
            autoComplete="username"
            className={styles.inputItem}
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[{ required: true, validator: validPassword, message: t('请输入密码') }]}
        >
          <Input.Password
            prefix={iconPwd}
            placeholder={t('请输入密码')}
            size="large"
            autoComplete="current-password"
            className={styles.inputItem}
            visibilityToggle={{
              visible: showPwd,
              onVisibleChange: setShowPwd,
              // visibleIcon: <span className="iconfont icon-tianchongxing-17" />,
              // hiddenIcon: <span className="iconfont icon-tianchongxing-4" />,
            }}
          />
        </Form.Item>
        {enableMFA ? (
          <>
            <Form.Item name="phone" rules={[{ required: true, validator: validPhoneNo }]}>
              <Input
                className={styles['form-input']}
                size="large"
                placeholder={t('请输入登录手机号')}
                prefix={
                  <span
                    style={{ fontWeight: 600 }}
                    className={classNames(styles['form-icon'], 'iconfont icon-a-3')}
                  />
                }
                onChange={(e) => setPhoneNo(e.target.value)}
              />
            </Form.Item>
            <Form.Item name="captcha" rules={[{ required: true, message: t('验证码不能为空') }]}>
              <Input
                className={styles['form-input']}
                size="large"
                placeholder={t('请输入登录验证码')}
                prefix={<span className={classNames(styles['form-icon'], 'iconfont icon-a-4')} />}
                suffix={
                  <Button
                    type="link"
                    size="small"
                    disabled={!canSendCode || !validIsMobile(phoneNo)}
                    onClick={() => handleSendSmsCode()}
                  >
                    {canSendCode ? t('获取验证码') : `${timeLeft}s${t('后重新获取')}`}
                  </Button>
                }
              />
            </Form.Item>
          </>
        ) : null}
        <div className={styles.forgetPwd}>
          <a onClick={onForget}>{t('忘记密码')}</a>
        </div>
        <Form.Item
          className={styles.agreeWrap}
          rules={[
            {
              validator: agreementValid,
            },
          ]}
        >
          <Checkbox checked={agree} onChange={onCheckboxChange}>
            {t('已阅读并同意')}
          </Checkbox>
          <span
            className={styles.protocol}
            onClick={() =>
              window.open(`${window.location.pathname}#/login/user-protocol?id=1`, '_blank')
            }
          >
            {t('《用户服务协议》')}
            {t('和')}
          </span>
          <span
            className={styles.protocol}
            onClick={() =>
              window.open(`${window.location.pathname}#/login/user-protocol?id=2`, '_blank')
            }
          >
            {t('《隐私政策》')}
          </span>
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            className={styles.loginBtn}
            loading={loading || loadingSetting}
            size="large"
            htmlType="submit"
          >
            {t('登 录')}
          </Button>
        </Form.Item>
      </Form>
      <ProtocolModal
        ref={protocolModalRef}
        loading={loading}
        onConfirm={(values, flag) => {
          setAgree(flag);
          onLogin(values);
        }}
        onCancel={setAgree}
      />
    </>
  );
};

export default AccountForm;
